// src/pages/CreateFaculty.jsx
import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api, mapRows } from "../utils/api";
import { useAuth } from "../context/AuthContext";
import { useDarkMode, DM } from "../context/DarkModeContext";
import DashboardHeader from "../components/DashboardHeader";
import { INDIAN_STATES_DISTRICTS } from "../constants/indiaStates";

// Compress image to base64 (max 120x120, 50% quality) for Firestore storage
function compressImageToBase64(file) {
  return new Promise((resolve, reject) => {
    const timeout = setTimeout(() => reject(new Error("Image processing timed out. Please try a smaller image.")), 8000);
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        clearTimeout(timeout);
        const maxSize = 120; 
        let w = img.width, h = img.height;
        if (w > h) { if (w > maxSize) { h = Math.round(h * maxSize / w); w = maxSize; } }
        else { if (h > maxSize) { w = Math.round(w * maxSize / h); h = maxSize; } }
        const canvas = document.createElement('canvas');
        canvas.width = w; canvas.height = h;
        const ctx = canvas.getContext('2d');
        if (!ctx) { reject(new Error("Browser does not support canvas scaling")); return; }
        ctx.drawImage(img, 0, 0, w, h);
        try {
          // Use low quality to keep string length very short
          resolve(canvas.toDataURL('image/jpeg', 0.5));
        } catch (err) {
          reject(new Error("Failed to convert image to data URL."));
        }
      };
      img.onerror = () => { clearTimeout(timeout); reject(new Error("Failed to load image file.")); };
      img.src = e.target.result;
    };
    reader.onerror = () => { clearTimeout(timeout); reject(new Error("Failed to read image file.")); };
    reader.readAsDataURL(file);
  });
}


export default function CreateFaculty() {
  const navigate = useNavigate();
  const { createFacultyAccount, userRole, currentUser } = useAuth();
  const { darkMode, toggleDarkMode } = useDarkMode();
  const d = darkMode;

  const [form, setForm] = useState({ institution: "Adhiparasakthi Dental College and Hospital" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [departments, setDepartments] = useState([]);
  const [colleges, setColleges] = useState(["Adhiparasakthi Dental College and Hospital"]);
  const [designations, setDesignations] = useState([]);
  const [fields, setFields] = useState([]);
  const [profileImage, setProfileImage] = useState(null);
  const [profileImagePreview, setProfileImagePreview] = useState(null);
  const fileInputRef = useRef(null);
  
  const [eduCourses, setEduCourses] = useState([]);
  const [eduDegrees, setEduDegrees] = useState([]);
  const [eduStreams, setEduStreams] = useState([]);
  const [eduBoards, setEduBoards] = useState([]);
  const [eduStateCouncils, setEduStateCouncils] = useState([]);

  const [educationalQualifications, setEducationalQualifications] = useState([
    { course: "", degree: "", major: "", allied: "", stream: "", yop: "", percentage: "", institution: "", board: "", attempts: 1, stateCouncil: "", regNo: "", regDate: "" }
  ]);
  const [workExperiences, setWorkExperiences] = useState([
    { name: "", jobTitle: "", fromDate: "", toDate: "", description: "", totalExp: "", attachments: "" }
  ]);
  const [designationHistory, setDesignationHistory] = useState([
    { dept: "", designation: "", unit: "", fromDate: "", toDate: "", isMain: true }
  ]);

  const [pendingFiles, setPendingFiles] = useState({});

  const handleEduChange = (index, field, value) => {
    const newData = [...educationalQualifications];
    newData[index][field] = value;
    setEducationalQualifications(newData);
  };
  const addEduRow = () => setEducationalQualifications([...educationalQualifications, { course: "", degree: "", major: "", allied: "", stream: "", yop: "", percentage: "", institution: "", board: "", attempts: 1, stateCouncil: "", regNo: "", regDate: "" }]);
  const removeEduRow = (index) => setEducationalQualifications(educationalQualifications.filter((_, i) => i !== index));

  const handleWorkChange = (index, field, value) => {
    const newData = [...workExperiences];
    newData[index][field] = value;
    setWorkExperiences(newData);
  };
  const addWorkRow = () => setWorkExperiences([...workExperiences, { name: "", jobTitle: "", fromDate: "", toDate: "", description: "", totalExp: "", attachments: "" }]);
  const removeWorkRow = (index) => setWorkExperiences(workExperiences.filter((_, i) => i !== index));

  const handleDesigHistoryChange = (index, field, value) => {
    const newData = [...designationHistory];
    if (field === "isMain" && value === true) {
      newData.forEach((item, i) => item.isMain = (i === index));
      // Sync with core profile if it's the main designation
      set("department", newData[index].dept);
      set("designation", newData[index].designation);
    } else {
      newData[index][field] = value;
    }
    setDesignationHistory(newData);
  };
  const addDesigHistoryRow = () => setDesignationHistory([...designationHistory, { dept: "", designation: "", unit: "", fromDate: "", toDate: "", isMain: false }]);
  const removeDesigHistoryRow = (index) => setDesignationHistory(designationHistory.filter((_, i) => i !== index));

  useEffect(() => {
    async function loadData() {
      try {
        const [dRes, cRes, desRes, ffRes] = await Promise.all([
          api.get("/departments?orderBy=name"),
          api.get("/colleges?orderBy=name"),
          api.get("/designations?orderBy=name"),
          api.get("/faculty_profile_fields?orderBy=order"),
        ]);

        setDepartments(mapRows(dRes).map((d) => d.name).filter(Boolean));
        setColleges([
          "Adhiparasakthi Dental College and Hospital",
          ...mapRows(cRes).map((d) => d.name).filter(Boolean),
        ]);
        setDesignations(mapRows(desRes).map((d) => d.name).filter(Boolean));

        const fetchListNoOrder = async (coll) => {
          const res = await api.get(`/${coll}`);
          return mapRows(res)
            .map((d) => d.name)
            .filter(Boolean)
            .sort((a, b) => a?.localeCompare(b));
        };
        setEduCourses(await fetchListNoOrder("edu_courses"));
        setEduDegrees(await fetchListNoOrder("edu_degrees"));
        setEduStreams(await fetchListNoOrder("edu_streams"));
        setEduBoards(await fetchListNoOrder("edu_boards"));
        setEduStateCouncils(await fetchListNoOrder("edu_state_councils"));

        setFields(mapRows(ffRes));
      } catch (err) {
        console.error(err);
      }
    }
    loadData();
  }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  async function handleCreate() {
    // Helper: get the key used in form for a field by its canonical name variants
    const getFieldKey = (nameVariants) => {
      for (const variant of nameVariants) {
        const found = fields.find(f => f.name?.toLowerCase().trim() === variant.toLowerCase());
        if (found) return found.key || found.name.toLowerCase().replace(/[^a-z0-9]/g, "_");
      }
      return null;
    };

    // Resolve actual keys used for core fields from dynamic field definitions
    const nameKey = getFieldKey(["staff name", "name"]) || "name";
    const empKey = getFieldKey(["staff id", "emp id", "employee id", "empid"]) || "empId";
    const mobileKey = getFieldKey(["mobile", "contact number", "phone", "mobile number"]) || "mobile";
    const deptKey = getFieldKey(["department", "departments", "dept"]) || "department";
    const desigKey = getFieldKey(["designation", "designations"]) || "designation";

    const nameVal = form[nameKey] || form.name || "";
    const empVal = form[empKey] || form.empId || "";
    const mobileVal = form[mobileKey] || form.mobile || "";
    
    // Auto-resolve Department & Designation from Designation History if not directly in form
    const mainDesig = designationHistory.find(d => d.isMain) || designationHistory[0] || {};
    const deptVal = (form[deptKey] || form.department || form.departmentName || mainDesig.dept || "").toString().trim();
    const desigVal = form[desigKey] || form.designation || mainDesig.designation || "";

    if (!nameVal || !form.email || !form.password || !deptVal || !empVal || !mobileVal || !desigVal) {
      return setError(`Please fill all required core fields. Missing: ${!nameVal?'Name, ':''}${!form.email?'Email, ':''}${!form.password?'Password, ':''}${!empVal?'Emp ID, ':''}${!mobileVal?'Mobile, ':''}${!deptVal?'Department (in Job Details), ':''}${!desigVal?'Designation (in Job Details)':''}`);
    }
    if (form.password.length < 6) return setError("Password must be at least 6 characters");
    if (!/^[0-9]{10}$/.test(mobileVal)) return setError("Mobile number must be 10 digits");

    // Normalize core field aliases so Firestore document always has canonical keys
    const coreAliases = {};
    if (nameKey !== "name") coreAliases.name = nameVal;
    if (empKey !== "empId") coreAliases.empId = empVal;
    if (mobileKey !== "mobile") coreAliases.mobile = mobileVal;
    if (deptKey !== "department") coreAliases.department = deptVal;
    if (desigKey !== "designation") coreAliases.designation = desigVal;

    // Check custom required fields (skip fields that are empty but not actually required)
    for (const f of fields) {
      if (f.required) {
        const key = f.key || f.name.toLowerCase().replace(/[^a-z0-9]/g, "_");
        if (!form[key] || !form[key].toString().trim()) {
          return setError(`Field "${f.name}" (${f.group || 'General'}) is required.`);
        }
      }
    }

    try {
      setLoading(true);
      setError("");
      
      // Default empty arrays for user sections
      const defaultSections = {
        articlePublications: [], textBookPublications: [], patents: [], copyrights: [],
        fellowships: [], phd: [], swayamCourses: [], awards: [], innovativeTeaching: [],
        eContent: [], researchGrants: [], guestLectures: [], conferencePresentation: [], shortResearchProjects: [],
      };

      // Compress image to base64 and store in Firestore directly (no Storage permission needed)
      let profilePhotoURL = "";
      if (profileImage) {
        profilePhotoURL = await compressImageToBase64(profileImage);
      }

      // Ensure keys map correctly, `name`, `mobile`, `empId`, `department`, `designation`, `institution`
      const profileData = { 
        ...defaultSections, 
        ...form,
        ...coreAliases,
        department: deptVal,
        departmentName: deptVal,
        ...(deptKey !== "department" ? { [deptKey]: deptVal } : {}),
        educationalQualifications,
        workExperiences,
        designationHistory,
        ...(profilePhotoURL ? { profilePhotoURL } : {}),
        createdAt: new Date().toISOString()
      };
      
      for (const [key, file] of Object.entries(pendingFiles)) {
        if (file) {
          const formData = new FormData();
          formData.append("file", file);
          const token = localStorage.getItem("portfolio_token");
          const uploadRes = await fetch("/api/upload", {
            method: "POST",
            headers: token ? { Authorization: `Bearer ${token}` } : {},
            body: formData,
          });
          if (!uploadRes.ok) throw new Error("File upload failed");
          const { url } = await uploadRes.json();
          profileData[key] = url;
        }
      }

      await createFacultyAccount(form.email, form.password, profileData);
      
      navigate("/admin/users", { state: { toast: `✅ Faculty account "${nameVal}" created successfully` } });
    } catch (err) {
      console.error("Create Error:", err);
      alert("Error: " + err.message);
      setError("❌ " + (err.message || "Failed to create faculty"));
    } finally {
      setLoading(false);
    }
  }

  // Grouping fields logically
  const groupedFields = fields.reduce((acc, field) => {
    const g = field.group?.trim() || "General Info";
    if (!acc[g]) acc[g] = [];
    acc[g].push(field);
    return acc;
  }, {});

  // Define input style variables
  const inputStyle = { width: "100%", background: DM.inputBg(d), border: `1.5px solid ${DM.inputBorder(d)}`, borderRadius: 10, padding: "10px 14px", fontSize: 15, color: DM.textPrimary(d), outline: "none", boxSizing: "border-box", fontFamily: "Georgia, serif", transition: "all 0.2s" };
  const labelStyle = { fontSize: 13, fontWeight: 700, color: DM.textSecondary(d), display: "block", marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.5 };

  // Helper to render an input for a specific field definition
  const renderFieldInput = (f, groupFields = fields) => {
    const k = f.key || f.name.toLowerCase().replace(/[^a-z0-9]/g, "_");
    
    const fieldName = f.name.toLowerCase().trim();
    if (fieldName === "title" || fieldName === "salutation" || fieldName === "prefix") {
      return (
        <select value={form[k] || ""} onChange={e => set(k, e.target.value)} style={{ ...inputStyle, cursor: "pointer" }}>
          <option value="">-- Select Title --</option>
          <option value="Dr.">Dr.</option>
          <option value="Mr.">Mr.</option>
          <option value="Ms.">Ms.</option>
          <option value="Mrs.">Mrs.</option>
          <option value="Prof.">Prof.</option>
        </select>
      );
    }

    // Match both singular and plural variants (e.g. "department" or "departments")
    if (k === "department" || k === "departments" || k === "dept") {
      return (
        <select value={form[k] || ""} onChange={e => set(k, e.target.value)} style={{ ...inputStyle, cursor: "pointer" }}>
          <option value="">Select Department...</option>
          {departments.map(opt => <option key={opt} value={opt}>{opt}</option>)}
        </select>
      );
    }
    if (k === "institution") {
      return (
        <select value={form.institution || "Adhiparasakthi Dental College and Hospital"} onChange={e => set("institution", e.target.value)} style={{ ...inputStyle, cursor: "pointer" }}>
          {colleges.map(opt => <option key={opt} value={opt}>{opt}</option>)}
        </select>
      );
    }
    if (k === "designation" || k === "designations") {
      return (
        <select value={form[k] || ""} onChange={e => set(k, e.target.value)} style={{ ...inputStyle, cursor: "pointer" }}>
          <option value="">Select Designation...</option>
          {designations.map(opt => <option key={opt} value={opt}>{opt}</option>)}
        </select>
      );
    }

    // Auto-load Logic for State and District
    if (fieldName === "state" || fieldName === "state/ut") {
      return (
        <select value={form[k] || ""} onChange={e => {
          set(k, e.target.value);
          // Find district key and clear it when state changes
          const dField = groupFields.find(fd => fd.name.toLowerCase().trim() === "district");
          if (dField) set(dField.key || dField.name.toLowerCase().replace(/[^a-z0-9]/g, "_"), "");
        }} style={{ ...inputStyle, cursor: "pointer" }}>
          <option value="">-- Select State --</option>
          {Object.keys(INDIAN_STATES_DISTRICTS).map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      );
    }

    if (fieldName === "district") {
      const sField = groupFields.find(fd => fd.name.toLowerCase().trim() === "state" || fd.name.toLowerCase().trim() === "state/ut");
      const sKey = sField ? (sField.key || sField.name.toLowerCase().replace(/[^a-z0-9]/g, "_")) : "state";
      const selectedState = form[sKey];
      const distOptions = selectedState && INDIAN_STATES_DISTRICTS[selectedState] ? INDIAN_STATES_DISTRICTS[selectedState] : [];

      return (
        <select value={form[k] || ""} onChange={e => set(k, e.target.value)} style={{ ...inputStyle, cursor: "pointer" }} disabled={!selectedState}>
          <option value="">-- Select District --</option>
          {distOptions.map(d => <option key={d} value={d}>{d}</option>)}
        </select>
      );
    }

    // Standard processing based on field type
    if (f.type === "Dropdown") {
      return (
        <select value={form[k] || ""} onChange={e => set(k, e.target.value)} style={{ ...inputStyle, cursor: "pointer" }}>
          <option value="">-- Select --</option>
          {(f.options || []).map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      );
    }
    if (f.type === "Textarea") {
      return <textarea value={form[k] || ""} onChange={e => set(k, e.target.value)} rows={3} style={{ ...inputStyle, resize: "vertical" }} />;
    }
    if (f.type === "Image") {
      return (
        <div>
          <input type="file" accept="image/*" onChange={e => {
            const file = e.target.files[0];
            if (file) {
              if (file.size > 200 * 1024) {
                alert("❌ Image size must be below 200KB");
                e.target.value = "";
                return;
              }
              if (!file.type.startsWith("image/")) {
                alert("❌ Please select a valid image file");
                e.target.value = "";
                return;
              }
              setPendingFiles(prev => ({ ...prev, [k]: file }));
            }
          }} style={{ ...inputStyle, padding: "6px 12px" }} />
          {pendingFiles[k] && (
            <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 10 }}>
              <img src={URL.createObjectURL(pendingFiles[k])} alt="Preview" style={{ width: 50, height: 50, objectFit: "cover", borderRadius: 8 }} />
              <div style={{ fontSize: 12, color: "#16a34a" }}>✅ {(pendingFiles[k].size / 1024).toFixed(1)}KB</div>
            </div>
          )}
        </div>
      );
    }

    const htmlType = f.type === "Number" ? "number" : f.type === "Date" ? "date" : f.type === "Month-Year" ? "month" : "text";
    return <input type={htmlType} value={form[k] || ""} onChange={e => set(k, e.target.value)} style={inputStyle} />;
  };

  return (
    <div style={{ minHeight: "100vh", background: DM.pageBg(d), fontFamily: "Georgia, serif" }}>

      <DashboardHeader label="Create Faculty">
        <button onClick={() => navigate("/admin/users")} style={{ background: "#fff", color: "#1d4ed8", border: "none", borderRadius: 10, padding: "8px 18px", fontWeight: 800, fontSize: 16, cursor: "pointer" }}>← Back to Users</button>
        <button onClick={toggleDarkMode} style={{ background: "rgba(255,255,255,0.15)", color: "#fff", border: "1px solid rgba(255,255,255,0.3)", borderRadius: 10, padding: "8px 14px", fontWeight: 600, fontSize: 15, cursor: "pointer" }}>{darkMode ? "☀️ Light" : "🌙 Dark"}</button>
      </DashboardHeader>

      <div style={{ padding: "32px 40px", maxWidth: 1200, margin: "0 auto" }}>
        
        <div style={{ marginBottom: 28 }}>
          <h1 style={{ fontSize: 30, fontWeight: 900, color: DM.textPrimary(d), margin: "0 0 6px" }}>➕ Create New Faculty</h1>
          <p style={{ color: DM.textMuted(d), margin: 0, fontSize: 16 }}>Fill in the details to setup a new staff portfolio and portal login.</p>
        </div>

        {error && (
          <div style={{ background: "#fef2f2", color: "#dc2626", border: "1px solid #fecaca", padding: "16px 20px", borderRadius: 12, fontSize: 16, fontWeight: 700, marginBottom: 24, boxShadow: "0 4px 12px rgba(220,38,38,0.1)" }}>
            {error}
          </div>
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>

          <div style={{ background: DM.cardBg(d), borderRadius: 16, padding: "24px", boxShadow: DM.shadow(d), borderTop: "4px solid #2563eb" }}>
            <h3 style={{ margin: "0 0 20px 0", fontSize: 18, fontWeight: 800, color: DM.textPrimary(d) }}>Login Credentials &amp; Profile Photo</h3>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16, alignItems: "start" }}>
              <div style={{ gridColumn: "span 1" }}>
                <label style={labelStyle}>Email Address (Login ID) *</label>
                <input type="email" value={form.email || ""} onChange={e => set("email", e.target.value)} placeholder="faculty@example.com" style={inputStyle} autoComplete="new-email" />
              </div>
              <div style={{ gridColumn: "span 1" }}>
                <label style={labelStyle}>Password *</label>
                <input type="text" value={form.password || ""} onChange={e => set("password", e.target.value)} placeholder="Min 6 chars" style={inputStyle} autoComplete="new-password" />
              </div>
              <div style={{ gridColumn: "span 1" }}>
                <label style={labelStyle}>Mobile Number *</label>
                <input type="text" value={form.mobile || ""} onChange={e => set("mobile", e.target.value)} placeholder="10 Digits" style={inputStyle} />
              </div>
              <div style={{ gridColumn: "span 1", display: "flex", alignItems: "center", gap: 16 }}>
                <div
                  onClick={() => fileInputRef.current?.click()}
                  style={{ width: 80, height: 80, borderRadius: "50%", border: `2px dashed ${DM.inputBorder(d)}`, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", overflow: "hidden", background: DM.inputBg(d), flexShrink: 0, transition: "all 0.2s" }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = "#2563eb"}
                  onMouseLeave={e => e.currentTarget.style.borderColor = DM.inputBorder(d)}
                >
                  {profileImagePreview
                    ? <img src={profileImagePreview} alt="Preview" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    : <span style={{ fontSize: 30, opacity: 0.4 }}>📷</span>
                  }
                </div>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>Profile Photo</label>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    style={{ display: "none" }}
                    onChange={e => {
                      const file = e.target.files[0];
                      if (file) {
                        setProfileImage(file);
                        setProfileImagePreview(URL.createObjectURL(file));
                      }
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    style={{ background: "#eaf1ff", color: "#2563eb", border: "1.5px solid #bfdbfe", borderRadius: 8, padding: "7px 14px", fontWeight: 700, fontSize: 14, cursor: "pointer", display: "block", marginBottom: 4 }}
                  >
                    {profileImagePreview ? "🔄 Change Photo" : "📤 Upload Photo"}
                  </button>
                  {profileImage && <div style={{ fontSize: 13, color: "#16a34a", fontWeight: 600 }}>✅ {profileImage.name}</div>}
                  {profileImagePreview && (
                    <button
                      type="button"
                      onClick={() => { setProfileImage(null); setProfileImagePreview(null); }}
                      style={{ background: "none", border: "none", color: "#dc2626", fontSize: 13, cursor: "pointer", padding: 0, fontWeight: 600, marginTop: 2 }}
                    >
                      ✕ Remove
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {Object.entries(groupedFields).sort((a, b) => {
            const minA = Math.min(...a[1].map(f => typeof f.order === "number" ? f.order : 9999));
            const minB = Math.min(...b[1].map(f => typeof f.order === "number" ? f.order : 9999));
            return minA !== minB ? minA - minB : a[0].localeCompare(b[0]);
          }).map(([groupName, groupFields]) => (
            <div key={groupName} style={{ background: DM.cardBg(d), borderRadius: 16, padding: "24px", boxShadow: DM.shadow(d), borderTop: "4px solid #10b981" }}>
              <h3 style={{ margin: "0 0 20px 0", fontSize: 18, fontWeight: 800, color: DM.textPrimary(d) }}>{groupName}</h3>
              
              <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16 }}>
                
                {groupFields.map(f => (
                  <div key={f.id} style={{ gridColumn: f.type === "Textarea" ? "1 / -1" : "auto" }}>
                    <label style={labelStyle}>
                      {f.name} {f.required && <span style={{ color: "#ef4444" }}>*</span>}
                    </label>
                    {renderFieldInput(f, groupFields)}
                  </div>
                ))}

              </div>
            </div>
          ))}

        </div>

        <div style={{ background: DM.cardBg(d), borderRadius: 16, padding: "24px", boxShadow: DM.shadow(d), borderTop: "4px solid #2563eb", marginTop: 24, marginBottom: 24 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: DM.textPrimary(d) }}>Job Details / Designation History</h3>
          </div>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 900 }}>
              <thead>
                <tr style={{ background: DM.inputBg(d), color: DM.textSecondary(d), fontSize: 14, textAlign: "left" }}>
                  <th style={{ padding: "10px 8px" }}>S.No</th>
                  <th style={{ padding: "10px 8px", minWidth: 150 }}>Department</th>
                  <th style={{ padding: "10px 8px", minWidth: 150 }}>Designation</th>
                  <th style={{ padding: "10px 8px", minWidth: 100 }}>Unit</th>
                  <th style={{ padding: "10px 8px", minWidth: 120 }}>From</th>
                  <th style={{ padding: "10px 8px", minWidth: 120 }}>To</th>
                  <th style={{ padding: "10px 8px", width: 60, textAlign: "center" }}>Main</th>
                  <th style={{ padding: "10px 8px", width: 40 }}></th>
                </tr>
              </thead>
              <tbody>
                {designationHistory.map((des, idx) => (
                  <tr key={idx} style={{ borderBottom: `1px solid ${DM.border(d)}` }}>
                    <td style={{ padding: "8px", fontSize: 16 }}>{idx + 1}</td>
                    <td style={{ padding: "8px" }}>
                      <select value={des.dept} onChange={e => handleDesigHistoryChange(idx, "dept", e.target.value)} style={{ ...inputStyle, padding: "6px 10px", cursor: "pointer" }}>
                        <option value="">--Sel--</option>
                        {departments.map(o => <option key={o} value={o}>{o}</option>)}
                      </select>
                    </td>
                    <td style={{ padding: "8px" }}>
                      <select value={des.designation} onChange={e => handleDesigHistoryChange(idx, "designation", e.target.value)} style={{ ...inputStyle, padding: "6px 10px", cursor: "pointer" }}>
                        <option value="">--Sel--</option>
                        {designations.map(o => <option key={o} value={o}>{o}</option>)}
                      </select>
                    </td>
                    <td style={{ padding: "8px" }}>
                       <select value={des.unit} onChange={e => handleDesigHistoryChange(idx, "unit", e.target.value)} style={{ ...inputStyle, padding: "6px 10px", cursor: "pointer" }}>
                        <option value="">--Sel--</option>
                        {["I", "II", "III", "IV", "V"].map(o => <option key={o} value={o}>{o}</option>)}
                      </select>
                    </td>
                    <td style={{ padding: "8px" }}><input type="date" value={des.fromDate} onChange={e => handleDesigHistoryChange(idx, "fromDate", e.target.value)} style={{ ...inputStyle, padding: "6px 10px" }} /></td>
                    <td style={{ padding: "8px" }}><input type="date" value={des.toDate} onChange={e => handleDesigHistoryChange(idx, "toDate", e.target.value)} style={{ ...inputStyle, padding: "6px 10px" }} /></td>
                    <td style={{ padding: "8px", textAlign: "center" }}>
                      <input type="checkbox" checked={des.isMain} onChange={e => handleDesigHistoryChange(idx, "isMain", e.target.checked)} style={{ width: 18, height: 18, cursor: "pointer" }} />
                    </td>
                    <td style={{ padding: "8px" }}>
                      {designationHistory.length > 1 && (
                        <button onClick={() => removeDesigHistoryRow(idx)} style={{ background: "#fef2f2", color: "#dc2626", border: "none", borderRadius: "6px", width: "28px", height: "28px", cursor: "pointer", fontSize: 18 }}>×</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}>
            <button onClick={addDesigHistoryRow} style={{ background: "#2563eb", color: "#fff", border: "none", borderRadius: "8px", width: "32px", height: "32px", fontSize: 22, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 2px 4px rgba(37,99,235,0.3)" }}>+</button>
          </div>
        </div>

        <div style={{ background: DM.cardBg(d), borderRadius: 16, padding: "24px", boxShadow: DM.shadow(d), borderTop: "4px solid #f97316", marginTop: 24, marginBottom: 24 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: DM.textPrimary(d) }}>Educational Qualification</h3>
          </div>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 900 }}>
              <thead>
                <tr style={{ background: DM.inputBg(d), color: DM.textSecondary(d), fontSize: 14, textAlign: "left" }}>
                  <th style={{ padding: "10px 8px" }}>S.No</th>
                  <th style={{ padding: "10px 8px", minWidth: 100 }}>Course</th>
                  <th style={{ padding: "10px 8px", minWidth: 100 }}>Degree</th>
                  <th style={{ padding: "10px 8px", minWidth: 100 }}>Major</th>
                  <th style={{ padding: "10px 8px", minWidth: 100 }}>Allied</th>
                  <th style={{ padding: "10px 8px", minWidth: 100 }}>Stream</th>
                  <th style={{ padding: "10px 8px", minWidth: 80 }}>YOP</th>
                  <th style={{ padding: "10px 8px", minWidth: 60 }}>%</th>
                  <th style={{ padding: "10px 8px", minWidth: 120 }}>Institution</th>
                  <th style={{ padding: "10px 8px", minWidth: 120 }}>Board/Univ</th>
                  <th style={{ padding: "10px 8px", minWidth: 80 }}>#Attempt(s)</th>
                  <th style={{ padding: "10px 8px", minWidth: 120 }}>State Council</th>
                  <th style={{ padding: "10px 8px", minWidth: 100 }}>Reg.No.</th>
                  <th style={{ padding: "10px 8px", minWidth: 120 }}>Reg.Date</th>
                  <th style={{ padding: "10px 8px", width: 40 }}></th>
                </tr>
              </thead>
              <tbody>
                {educationalQualifications.map((edu, idx) => (
                  <tr key={idx} style={{ borderBottom: `1px solid ${DM.border(d)}` }}>
                    <td style={{ padding: "8px", fontSize: 16 }}>{idx + 1}</td>
                    <td style={{ padding: "8px" }}>
                      <select value={edu.course} onChange={e => handleEduChange(idx, "course", e.target.value)} style={{ ...inputStyle, padding: "6px 10px", cursor: "pointer" }}>
                        <option value="">--Sel--</option>
                        {eduCourses.map(o => <option key={o} value={o}>{o}</option>)}
                      </select>
                    </td>
                    <td style={{ padding: "8px" }}>
                      <select value={edu.degree} onChange={e => handleEduChange(idx, "degree", e.target.value)} style={{ ...inputStyle, padding: "6px 10px", cursor: "pointer" }}>
                        <option value="">--Sel--</option>
                        {eduDegrees.map(o => <option key={o} value={o}>{o}</option>)}
                      </select>
                    </td>
                    <td style={{ padding: "8px" }}><input type="text" value={edu.major} onChange={e => handleEduChange(idx, "major", e.target.value)} style={{ ...inputStyle, padding: "6px 10px" }} /></td>
                    <td style={{ padding: "8px" }}><input type="text" value={edu.allied} onChange={e => handleEduChange(idx, "allied", e.target.value)} style={{ ...inputStyle, padding: "6px 10px" }} /></td>
                    <td style={{ padding: "8px" }}>
                      <select value={edu.stream} onChange={e => handleEduChange(idx, "stream", e.target.value)} style={{ ...inputStyle, padding: "6px 10px", cursor: "pointer" }}>
                        <option value="">--Sel--</option>
                        {eduStreams.map(o => <option key={o} value={o}>{o}</option>)}
                      </select>
                    </td>
                    <td style={{ padding: "8px" }}><input type="text" value={edu.yop} onChange={e => handleEduChange(idx, "yop", e.target.value)} style={{ ...inputStyle, padding: "6px 10px" }} placeholder="YYYY" /></td>
                    <td style={{ padding: "8px" }}><input type="text" value={edu.percentage} onChange={e => handleEduChange(idx, "percentage", e.target.value)} style={{ ...inputStyle, padding: "6px 10px" }} /></td>
                    <td style={{ padding: "8px" }}><input type="text" value={edu.institution} onChange={e => handleEduChange(idx, "institution", e.target.value)} style={{ ...inputStyle, padding: "6px 10px" }} /></td>
                    <td style={{ padding: "8px" }}>
                      <select value={edu.board} onChange={e => handleEduChange(idx, "board", e.target.value)} style={{ ...inputStyle, padding: "6px 10px", cursor: "pointer" }}>
                        <option value="">--Sel--</option>
                        {eduBoards.map(o => <option key={o} value={o}>{o}</option>)}
                      </select>
                    </td>
                    <td style={{ padding: "8px" }}><input type="number" value={edu.attempts} onChange={e => handleEduChange(idx, "attempts", e.target.value)} style={{ ...inputStyle, padding: "6px 10px", width: "100%", minWidth: 60 }} min="1" /></td>
                    <td style={{ padding: "8px" }}>
                      <select value={edu.stateCouncil} onChange={e => handleEduChange(idx, "stateCouncil", e.target.value)} style={{ ...inputStyle, padding: "6px 10px" }}>
                        <option value="">-- Select --</option>
                        {eduStateCouncils.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </td>
                    <td style={{ padding: "8px" }}><input type="text" value={edu.regNo} onChange={e => handleEduChange(idx, "regNo", e.target.value)} style={{ ...inputStyle, padding: "6px 10px" }} /></td>
                    <td style={{ padding: "8px" }}><input type="date" value={edu.regDate} onChange={e => handleEduChange(idx, "regDate", e.target.value)} style={{ ...inputStyle, padding: "6px 10px" }} /></td>
                    <td style={{ padding: "8px" }}>
                      {educationalQualifications.length > 1 && (
                        <button onClick={() => removeEduRow(idx)} style={{ background: "#fef2f2", color: "#dc2626", border: "none", borderRadius: "6px", width: "28px", height: "28px", cursor: "pointer", fontSize: 18 }}>×</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}>
            <button onClick={addEduRow} style={{ background: "#f97316", color: "#fff", border: "none", borderRadius: "8px", width: "32px", height: "32px", fontSize: 22, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 2px 4px rgba(249,115,22,0.3)" }}>+</button>
          </div>
        </div>

        <div style={{ background: DM.cardBg(d), borderRadius: 16, padding: "24px", boxShadow: DM.shadow(d), borderTop: "4px solid #f97316", marginBottom: 24 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: DM.textPrimary(d) }}>Work Experience</h3>
          </div>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 900 }}>
              <thead>
                <tr style={{ background: DM.inputBg(d), color: DM.textSecondary(d), fontSize: 14, textAlign: "left" }}>
                  <th style={{ padding: "10px 8px" }}>S.No</th>
                  <th style={{ padding: "10px 8px", minWidth: 150 }}>Name</th>
                  <th style={{ padding: "10px 8px", minWidth: 150 }}>Job Title</th>
                  <th style={{ padding: "10px 8px", minWidth: 120 }}>From</th>
                  <th style={{ padding: "10px 8px", minWidth: 120 }}>To</th>
                  <th style={{ padding: "10px 8px", minWidth: 200 }}>Description</th>
                  <th style={{ padding: "10px 8px", minWidth: 80 }}>Total Exp in Years</th>
                  <th style={{ padding: "10px 8px", minWidth: 120 }}>Attachments</th>
                  <th style={{ padding: "10px 8px", width: 40 }}></th>
                </tr>
              </thead>
              <tbody>
                {workExperiences.map((work, idx) => (
                  <tr key={idx} style={{ borderBottom: `1px solid ${DM.border(d)}` }}>
                    <td style={{ padding: "8px", fontSize: 16 }}>{idx + 1}</td>
                    <td style={{ padding: "8px" }}><input type="text" value={work.name} onChange={e => handleWorkChange(idx, "name", e.target.value)} style={{ ...inputStyle, padding: "6px 10px" }} /></td>
                    <td style={{ padding: "8px" }}><input type="text" value={work.jobTitle} onChange={e => handleWorkChange(idx, "jobTitle", e.target.value)} style={{ ...inputStyle, padding: "6px 10px" }} /></td>
                    <td style={{ padding: "8px" }}><input type="date" value={work.fromDate} onChange={e => handleWorkChange(idx, "fromDate", e.target.value)} style={{ ...inputStyle, padding: "6px 10px" }} /></td>
                    <td style={{ padding: "8px" }}><input type="date" value={work.toDate} onChange={e => handleWorkChange(idx, "toDate", e.target.value)} style={{ ...inputStyle, padding: "6px 10px" }} /></td>
                    <td style={{ padding: "8px" }}><input type="text" value={work.description} onChange={e => handleWorkChange(idx, "description", e.target.value)} style={{ ...inputStyle, padding: "6px 10px" }} /></td>
                    <td style={{ padding: "8px" }}><input type="number" step="0.1" value={work.totalExp} onChange={e => handleWorkChange(idx, "totalExp", e.target.value)} style={{ ...inputStyle, padding: "6px 10px", width: "100%", minWidth: 80 }} /></td>
                    <td style={{ padding: "8px" }}><input type="text" placeholder="Link/File" value={work.attachments} onChange={e => handleWorkChange(idx, "attachments", e.target.value)} style={{ ...inputStyle, padding: "6px 10px" }} /></td>
                    <td style={{ padding: "8px" }}>
                      {workExperiences.length > 1 && (
                        <button onClick={() => removeWorkRow(idx)} style={{ background: "#fef2f2", color: "#dc2626", border: "none", borderRadius: "6px", width: "28px", height: "28px", cursor: "pointer", fontSize: 18 }}>×</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}>
            <button onClick={addWorkRow} style={{ background: "#f97316", color: "#fff", border: "none", borderRadius: "8px", width: "32px", height: "32px", fontSize: 22, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 2px 4px rgba(249,115,22,0.3)" }}>+</button>
          </div>
        </div>

        {/* Action Buttons */}
        <div style={{ display: "flex", gap: 16, marginTop: 32, justifyContent: "flex-end", background: DM.cardBg(d), padding: "20px 24px", borderRadius: 16, boxShadow: DM.shadow(d) }}>
          <button onClick={() => navigate("/admin/users")} style={{ padding: "12px 24px", background: DM.inputBg(d), color: DM.textPrimary(d), border: `1px solid ${DM.border(d)}`, borderRadius: 12, fontWeight: 800, fontSize: 16, cursor: "pointer", transition: "all 0.2s" }}>
            Cancel
          </button>
          <button onClick={handleCreate} disabled={loading} style={{ padding: "12px 32px", background: "linear-gradient(135deg, #1d4ed8, #2563eb)", color: "#fff", border: "none", borderRadius: 12, fontWeight: 800, fontSize: 16, cursor: loading ? "not-allowed" : "pointer", boxShadow: "0 4px 12px rgba(37,99,235,0.3)", transition: "all 0.2s" }}>
            {loading ? "⏳ Creating Faculty..." : "🚀 Create Faculty Account"}
          </button>
        </div>

      </div>
    </div>
  );
}
